import React, { useEffect, useRef, useState } from 'react';
import { Volume2, Loader2, Pause, Play, Crown } from 'lucide-react';
import { api, API_BASE } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Premium "Voice Reader" — uses /api/recipes/{id}/speak-stream which returns
 * raw mp3 (no base64). The browser starts playback as soon as the first bytes
 * arrive instead of waiting for the full TTS round-trip, eliminating the long
 * pause users were seeing.
 */
let CURRENT_AUDIO = null;

/**
 * Globally stop the active voice-reader audio (if any). Called from
 * CookMode and other components that want to take over audio output.
 */
export function stopGlobalVoice() {
  const a = CURRENT_AUDIO;
  if (!a) return;
  try {
    a.oncanplay = null;
    a.onended = null;
    a.onerror = null;
    a.onplay = null;
    a.onplaying = null;
    a.onloadeddata = null;
    a.pause();
    // Nuking the src + calling load() is the only reliable way across
    // Android WebView / iOS WKWebView / desktop browsers to fully release
    // the streaming connection. Without this, calling pause() on a
    // chunked HTTP stream often leaves the network/buffer alive and the
    // audio resumes a moment later.
    a.removeAttribute('src');
    a.load();
  } catch { /* noop */ }
  CURRENT_AUDIO = null;
}

export default function VoiceReader({ recipeId, stepIndex, compact = false }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  // Build the absolute streaming URL so <audio> can fetch it directly.
  // Auth: backend reads either the session cookie OR the Bearer token from
  // localStorage. We append the token as a query param when present so that
  // the native <audio> element (which doesn't carry custom headers) still
  // authenticates.
  const buildStreamUrl = () => {
    let token = '';
    try { token = localStorage.getItem('matbakhi_session_token') || ''; } catch { /* noop */ }
    const params = new URLSearchParams({
      language: lang,
      voice: lang === 'ar' ? 'shimmer' : 'nova',
    });
    if (typeof stepIndex === 'number') params.set('step_index', String(stepIndex));
    if (token) params.set('t', token);
    return `${API_BASE}/recipes/${recipeId}/speak-stream?${params.toString()}`;
  };

  useEffect(() => () => {
    if (audioRef.current) {
      const a = audioRef.current;
      try {
        a.oncanplay = null;
        a.onended = null;
        a.onerror = null;
        a.onplay = null;
        a.onplaying = null;
        a.onloadeddata = null;
        a.pause();
        a.removeAttribute('src');
        a.load();
      } catch { /* noop */ }
      if (CURRENT_AUDIO === a) CURRENT_AUDIO = null;
      audioRef.current = null;
    }
  }, []);

  const stop = () => {
    const a = audioRef.current;
    if (a) {
      // Detach ALL handlers first so any in-flight event (canplay,
      // playing, loadeddata, etc.) can't restart playback.
      a.oncanplay = null;
      a.onended = null;
      a.onerror = null;
      a.onplay = null;
      a.onplaying = null;
      a.onloadeddata = null;
      try {
        a.pause();
        // On Android WebView a chunked-stream <audio> will happily keep
        // buffering and resume after pause()+currentTime=0. The only
        // 100%-reliable way to stop it is to drop the src and reload.
        a.removeAttribute('src');
        a.load();
      } catch { /* noop */ }
      audioRef.current = null;
    }
    if (CURRENT_AUDIO === a) CURRENT_AUDIO = null;
    setPlaying(false);
    setLoading(false);
  };

  const play = async () => {
    if (!user) { navigate('/login'); return; }
    if (loading) return;
    if (playing) { stop(); return; }
    if (!user.is_premium) {
      toast.info(t('needPremium'));
      navigate('/premium');
      return;
    }
    if (CURRENT_AUDIO) {
      try {
        CURRENT_AUDIO.oncanplay = null;
        CURRENT_AUDIO.onended = null;
        CURRENT_AUDIO.onerror = null;
        CURRENT_AUDIO.onplay = null;
        CURRENT_AUDIO.onplaying = null;
        CURRENT_AUDIO.onloadeddata = null;
        CURRENT_AUDIO.pause();
        CURRENT_AUDIO.removeAttribute('src');
        CURRENT_AUDIO.load();
      } catch { /* noop */ }
      CURRENT_AUDIO = null;
    }
    setLoading(true);
    try {
      const audio = new Audio(buildStreamUrl());
      audio.crossOrigin = 'use-credentials';
      audio.preload = 'auto';
      audioRef.current = audio;
      CURRENT_AUDIO = audio;
      let started = false;                                  // start playback only once
      audio.oncanplay = () => {
        if (started || audioRef.current !== audio) return;
        started = true;
        setLoading(false);
        audio.play().catch(() => {});
        setPlaying(true);
      };
      audio.onended = () => {
        if (audioRef.current === audio) {
          audioRef.current = null;
          if (CURRENT_AUDIO === audio) CURRENT_AUDIO = null;
          setPlaying(false);
        }
      };
      audio.onerror = () => {
        if (audioRef.current === audio) { audioRef.current = null; setPlaying(false); }
        setLoading(false);
        toast.error('Voice error');
      };
    } catch {
      setLoading(false);
      toast.error('Voice error');
    }
  };

  if (compact) {
    return (
      <button
        type="button"
        onClick={play}
        data-testid={`voice-read-step-${stepIndex ?? 'all'}`}
        className={`relative inline-flex items-center justify-center h-9 w-9 rounded-full transition ${
          playing
            ? 'bg-[#C05A42] text-white shadow-md'
            : 'bg-white text-[#C05A42] border border-black/10 hover:bg-[#EFEBE1] dark:bg-neutral-800 dark:text-[#E29F48] dark:border-white/10 dark:hover:bg-neutral-700'
        }`}
        aria-label={playing ? t('stop') : t('listen')}
        title={t('listen')}
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : playing ? <Pause className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        {!user?.is_premium && (
          <span className="absolute -top-1 -end-1 inline-flex items-center justify-center h-4 w-4 rounded-full bg-[#E29F48] text-white">
            <Crown className="h-2.5 w-2.5" />
          </span>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={play}
      data-testid="voice-read-all"
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition ${
        playing
          ? 'bg-[#C05A42] text-white shadow-md'
          : 'bg-white text-[#2C2420] border border-black/10 hover:bg-[#EFEBE1] dark:bg-neutral-800 dark:text-neutral-100 dark:border-white/10 dark:hover:bg-neutral-700'
      }`}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      {playing ? t('stop') : t('listenWhole')}
      {!user?.is_premium && <Crown className="h-3 w-3 text-[#E29F48]" />}
    </button>
  );
}
